import type { Page, Project } from './types';

/*
 * Page-level three-way merge for concurrent editing. `base` is the version
 * both sides started from, `mine` the local copy, `theirs` what is on the
 * server now. Pages are matched by id; a page changed on one side only takes
 * that side, a page changed on both (differently) is a conflict and keeps
 * theirs. Project-level fields merge the same way, field by field.
 */

export interface MergeResult {
  project: Project;
  /** ids of pages both sides changed differently (theirs was kept) */
  conflicts: string[];
  /** ids of pages that came from theirs (changed, added or removed there) */
  fromTheirs: string[];
}

const J = (v: unknown) => JSON.stringify(v ?? null);

export function mergeProjects(base: Project, mine: Project, theirs: Project, opt: { keepTheirs?: Set<string> } = {}): MergeResult {
  const keep = opt.keepTheirs ?? new Set<string>();
  const byId = (p: Project) => new Map(p.pages.map((pg) => [pg.id, pg]));
  const B = byId(base), M = byId(mine), T = byId(theirs);
  const conflicts: string[] = [], fromTheirs: string[] = [];

  // project-level fields other than pages / symbols
  const out: Project = { ...theirs };
  const keys = new Set([...Object.keys(base), ...Object.keys(mine), ...Object.keys(theirs)].filter((k) => k !== 'pages' && k !== 'symbols'));
  for (const k of keys) {
    const b = J((base as any)[k]), m = J((mine as any)[k]), t = J((theirs as any)[k]);
    if (m !== b && t === b) {
      if ((mine as any)[k] === undefined) delete (out as any)[k];
      else (out as any)[k] = (mine as any)[k];
    }
  }

  // pages: decide each id
  const pick = new Map<string, Page | null>();
  for (const id of new Set([...B.keys(), ...M.keys(), ...T.keys()])) {
    const b = B.get(id), m = M.get(id), t = T.get(id);
    const mc = J(m) !== J(b), tc = J(t) !== J(b);
    if (keep.has(id) || (tc && !mc)) {
      pick.set(id, t ?? null);
      if (tc) fromTheirs.push(id);
    } else if (mc && !tc) pick.set(id, m ?? null);
    else if (!mc && !tc) pick.set(id, t ?? m ?? null);
    else if (J(m) === J(t)) pick.set(id, t ?? null);
    else {
      conflicts.push(id);
      fromTheirs.push(id);
      pick.set(id, t ?? m ?? null);
    }
  }
  // order: mine's order, with pages only theirs has inserted after their predecessor there
  const order = mine.pages.map((p) => p.id);
  theirs.pages.forEach((p, i) => {
    if (order.includes(p.id)) return;
    const prev = i > 0 ? order.indexOf(theirs.pages[i - 1].id) : -1;
    order.splice(prev + 1, 0, p.id);
  });
  out.pages = order.map((id) => pick.get(id)).filter((p): p is Page => !!p);
  if (!out.pages.length) out.pages = theirs.pages;

  // user symbols: union by key, mine wins for keys only I changed
  const sym = new Map((theirs.symbols ?? []).map((s) => [s.key, s]));
  const bs = new Map((base.symbols ?? []).map((s) => [s.key, J(s)]));
  for (const s of mine.symbols ?? []) if (!sym.has(s.key) || bs.get(s.key) !== J(s)) sym.set(s.key, s);
  if (sym.size) out.symbols = [...sym.values()];
  return { project: out, conflicts, fromTheirs };
}

/** Ids of the pages whose content differs between two versions (added and removed included). */
export function changedPageIds(a: Project, b: Project): string[] {
  const A = new Map(a.pages.map((p) => [p.id, J(p)]));
  const out: string[] = [];
  for (const p of b.pages) if (A.get(p.id) !== J(p)) out.push(p.id);
  for (const id of A.keys()) if (!b.pages.some((p) => p.id === id)) out.push(id);
  return out;
}
